import {
  type OrderStatus,
  OrderStatusCanceled,
  OrderStatusExecuted,
  OrderStatusExpired,
  OrderStatusFailed,
  OrderStatusFilled,
  OrderStatusOpen,
  OrderStatusPartiallyFilled,
  OrderStatusPending,
  OrderStatusTriggered,
  OrderStatusUnspecified,
  OrderStatusUntriggered,
} from '@/api/spec.ts';
import { captureError } from '../../../utils/sentryHelpers';

/**
 * Everything the order flow needs to know about one `OrderStatus`, in one place — replacing five
 * arrays that had no way to stay consistent with each other.
 *
 * Four separate fields, not one verdict: `live` is about the row in Open Orders, `settles` is about
 * the request that produced it, and conflating them once dropped `Pending` trigger orders from the
 * table.
 */
export interface OrderStatusFacts {
  /** The order still exists and belongs in Open Orders. */
  live: boolean;
  /** The order is gone — evict the row whether or not the update carries `r`. */
  terminal: boolean;
  /** What this status decides about the request that produced the order, if anything. */
  settles: 'confirmed' | 'failed' | null;
  /** Raises its own order-level toast, so the posting must not raise a second one for it. */
  notifies: boolean;
}

/**
 * Per `api-docs-internal`, the first status in `{2,3,4,5,8,9,10}` is definitive for a request and
 * everything after it is ignored; `7` alone is a failure. `1` (Pending, how trigger orders are
 * announced) is deliberately outside both sets: news, not an outcome.
 *
 * `6` (Expired) is in neither documented set. Settled as `confirmed` on purpose — the exchange is
 * terminally done, so leaving it unresolved only strands the request until a timeout invents a
 * worse reason, and `notifies` means the user reads "expired" from the order toast anyway.
 */
const ORDER_STATUS_FACTS = new Map<OrderStatus, OrderStatusFacts>([
  [OrderStatusUnspecified, { live: false, terminal: false, settles: null, notifies: false }],
  [OrderStatusPending, { live: true, terminal: false, settles: null, notifies: false }],
  [OrderStatusOpen, { live: true, terminal: false, settles: 'confirmed', notifies: true }],
  // No toast: partial fills would clutter the feed. The posting's own success toast reports it.
  [OrderStatusPartiallyFilled, { live: true, terminal: false, settles: 'confirmed', notifies: false }],
  [OrderStatusFilled, { live: false, terminal: true, settles: 'confirmed', notifies: true }],
  [OrderStatusCanceled, { live: false, terminal: true, settles: 'confirmed', notifies: false }],
  [OrderStatusExpired, { live: false, terminal: true, settles: 'confirmed', notifies: true }],
  [OrderStatusFailed, { live: false, terminal: true, settles: 'failed', notifies: true }],
  [OrderStatusUntriggered, { live: true, terminal: false, settles: 'confirmed', notifies: false }],
  [OrderStatusTriggered, { live: true, terminal: false, settles: 'confirmed', notifies: true }],
  [OrderStatusExecuted, { live: false, terminal: true, settles: 'confirmed', notifies: false }],
]);

/** Inert: evicting would strand an order that still exists, settling would decide an unknown outcome. */
const UNKNOWN_STATUS_FACTS: OrderStatusFacts = { live: false, terminal: false, settles: null, notifies: false };

/** One report per unrecognised value, not one per update. */
const reportedUnknownStatuses = new Set<OrderStatus>();

/**
 * `spec.ts` types `OrderStatus` as a bare `number`, so a `switch` narrows nothing and `tsc` cannot
 * check this table. `orderStatusFacts.test.ts` enumerates the spec's constants instead, and this
 * reports anything that still reaches production unrecognised.
 */
export function getOrderStatusFacts(st: OrderStatus): OrderStatusFacts {
  const facts = ORDER_STATUS_FACTS.get(st);
  if (facts) return facts;

  if (!reportedUnknownStatuses.has(st)) {
    reportedUnknownStatuses.add(st);
    captureError(new Error('Unrecognised order status from the exchange'), 'order_status_unknown', { status: st });
  }
  return UNKNOWN_STATUS_FACTS;
}

/** Test-only: lets the totality test read the table without exporting a mutable map. */
export function knownOrderStatuses(): OrderStatus[] {
  return [...ORDER_STATUS_FACTS.keys()];
}
